/* ============================================================
 * header-overlay.js
 * ------------------------------------------------------------
 * Drives the transparent-over-hero header (see
 * assets/header-overlay.css.liquid for the styling).
 *
 * Three jobs:
 *   1. Publish the rendered header height as
 *      `--header-overlay-height` so the CSS can pull the hero
 *      section up underneath the header by exactly that much.
 *      This runs the moment the header markup is parsed - the
 *      tag is placed directly after the header sections in
 *      layout/theme.liquid - so the hero is already in the right
 *      place at first paint. Summing the two header sections gets
 *      close but not exact: margins between and below them, and
 *      sub-pixel rounding, are outside those boxes and leave a
 *      couple of pixels of background showing between the header
 *      and the hero. So the sum is only the first-paint estimate -
 *      once the hero exists, the distance between the two is
 *      measured directly instead.
 *   2. Toggle `header-overlay--scrolled` on <body> once the page
 *      has been scrolled, which switches the header back to the
 *      normal white background / dark text.
 *   3. Withdraw the whole treatment if the first section on the
 *      page turns out not to be a hero-style section. The theme
 *      marks those with `overlaid-header-option`; the same check
 *      the built-in overlay option uses (see z__jsHeader.js).
 * ============================================================ */
(function () {
  'use strict';

  var ENABLED_CLASS = 'header-overlay';
  var SCROLLED_CLASS = 'header-overlay--scrolled';
  var SCROLL_THRESHOLD = 10;

  var body = document.body;
  if (!body || body.className.indexOf(ENABLED_CLASS) === -1) {
    return;
  }

  var sections = [
    document.getElementById('shopify-section-header-centered__top-bar'),
    document.getElementById('shopify-section-header-centered')
  ];

  var announcement = document.getElementById('shopify-section-announcement-bar');

  /* A `display: none` element reports a zero rect at the origin, not
     its position - so anything measured off one silently reads as
     viewport-top. The top bar carries the `top-bar` schema class and
     `.top-bar` is display:none below 799px, which makes this the
     normal state on mobile rather than an edge case. */
  function isRendered(el) {
    return !!el && el.getClientRects().length > 0;
  }

  function firstRenderedSection() {
    for (var i = 0; i < sections.length; i++) {
      if (isRendered(sections[i])) {
        return sections[i];
      }
    }
    return null;
  }

  /* Where the hero's top edge belongs: immediately below the
     announcement bar, or the very top of the page when there is not
     one. Anchoring to the announcement bar rather than to the top of
     the first header section also takes in whatever sits between the
     two, which otherwise stays behind as a strip of page background
     above the hero. */
  function anchorTop() {
    if (isRendered(announcement)) {
      return announcement.getBoundingClientRect().bottom;
    }
    var first = firstRenderedSection();
    return first ? first.getBoundingClientRect().top : 0;
  }

  var hero = null;
  var height = 0;

  /* The pull-up is published two ways. `--header-overlay-height` drives
     the CSS rule, which is what positions the hero at first paint,
     before this script has ever seen it. From then on the margin is
     also written straight onto the element found below, so the effect
     does not depend on that CSS selector continuing to match the
     markup Shopify emits. */
  function setHeight(value) {
    height = Math.max(0, value);
    document.documentElement.style.setProperty('--header-overlay-height', height + 'px');
    if (hero) {
      hero.style.marginTop = '-' + height + 'px';
    }
    publishTopBarHeight();
    publishNavRowHeight();
  }

  /* The nav sticks at a hardcoded top: 50px while the top bar is
     actually 52px tall (.navbar min-height: 3.25rem), so as they pin
     the nav slides over the top bar's bottom 2px instead of stacking
     flush against it. Invisible while both are opaque white; plainly
     visible once they are transparent and moving over the hero. Hand
     the real height to the CSS so the two stay welded together.

     Zero on mobile, where the top bar is not rendered - which is also
     the right answer there, since the nav is position: relative at
     that breakpoint and any offset would shift it bodily. */
  function publishTopBarHeight() {
    var topBar = sections[0];
    var value = isRendered(topBar) ? topBar.getBoundingClientRect().height : 0;
    /* Rounded to a whole pixel. A fractional sticky offset against a
       fractional height lets the browser round the two rows'
       positions differently from one frame to the next, which flickers
       a hairline of hero between them while scrolling. */
    document.documentElement.style.setProperty('--header-overlay-topbar-height', Math.round(value) + 'px');
  }

  /* The nav row's own height, which is where the drop-down panels have
     to start if they are to meet the header shade rather than leave a
     sliver of hero between the two.

     They do not today. .header--centered is pinned to 48px
     (combine-overrides) while the nav items inside it come out at 56.5
     - 1rem of padding top and bottom around a 24.5px line - so they
     overflow the header box by 8.5px. The mega menu is unaffected,
     because z__jsMegaMenu.js makes its parent item position: static
     and its `top: 100%` therefore resolves against the section: the
     panel lands exactly on the header's bottom edge. The drop-down's
     parent stays position: relative, so its `top: 90%` resolves
     against the 56.5px item instead and lands ~3px lower.

     Hence this: the one number that turns the item-relative offset
     into a header-relative one. Both boxes share a top edge - the
     items are top-aligned in the row and all the overflow is at the
     bottom - so the nav row's height is exactly the offset from the
     item's top to where the panel belongs.

     Published rather than hardcoded at 48px so that the day the
     padding, the font or that override changes, the panels follow. */
  function publishNavRowHeight() {
    var navRow = sections[1];
    var value = isRendered(navRow) ? navRow.getBoundingClientRect().height : 0;
    document.documentElement.style.setProperty('--header-overlay-navrow-height', Math.round(value) + 'px');
  }

  /* A rough value for the very first paint, before the hero element
     exists to be measured against: the two header sections added
     together. Whichever of them is hidden at the current breakpoint
     (the top bar and desktop nav below 799px, the mobile header above
     it) measures 0, so this is right on both without repeating the
     media query here. It is superseded by measure() as soon as the
     hero is known. */
  function estimate() {
    var total = 0;
    for (var i = 0; i < sections.length; i++) {
      if (sections[i]) {
        total += sections[i].getBoundingClientRect().height;
      }
    }
    setHeight(total);
  }

  /* The exact pull-up, in one reading: the distance between where the
     hero naturally starts and where it belongs (see anchorTop).

     Taken with the margin neutralised, so it is an absolute
     measurement rather than a correction applied to an estimate. That
     matters because the shortfall an estimate leaves behind is
     whatever sits between those two points but inside neither box -
     margins, line boxes, sub-pixel rounding - and this reading
     contains all of it by definition, without having to know what it
     was made of. */
  function measure() {
    if (!hero) {
      estimate();
      return;
    }

    var offset = window.pageYOffset || document.documentElement.scrollTop || 0;
    if (offset > 1) {
      /* The header is stuck to the top of the viewport, so the
         distance between the two is no longer the layout distance.
         The margin already in place stays until we are back at the
         top, where applyScrollState() re-reads it. */
      return;
    }

    hero.style.marginTop = '0px';
    setHeight(hero.getBoundingClientRect().top - anchorTop());
  }

  var scrollQueued = false;
  var isScrolled = null;

  function applyScrollState() {
    scrollQueued = false;
    var offset = window.pageYOffset || document.documentElement.scrollTop || 0;
    var scrolled = offset > SCROLL_THRESHOLD;
    if (scrolled === isScrolled) {
      return;
    }
    isScrolled = scrolled;

    if (scrolled) {
      body.classList.add(SCROLLED_CLASS);
    } else {
      body.classList.remove(SCROLLED_CLASS);
      /* Back at the top: a resize part-way down the page will have
         been skipped, so take the reading again now that it is
         meaningful. */
      measure();
    }
  }

  function onScroll() {
    if (scrollQueued) {
      return;
    }
    scrollQueued = true;
    window.requestAnimationFrame(applyScrollState);
  }

  /* The hero has to be the first thing on the page for the header to
     have something to sit on top of. Found by document order rather
     than by a :first-child selector, so stray text nodes or anything
     an app injects into the container cannot stop it matching. */
  function findHero() {
    var container = document.querySelector('[data-check-for-order="true"]');
    if (!container) {
      return null;
    }
    var first = container.querySelector('[id^="shopify-section"]');
    if (!first || first.className.indexOf('overlaid-header-option') === -1) {
      return null;
    }
    if (first.innerHTML.trim().length === 0) {
      return null;
    }
    return first;
  }

  function refresh() {
    var found = findHero();

    if (!found) {
      if (hero) {
        hero.style.removeProperty('margin-top');
        hero = null;
      }
      body.classList.remove(ENABLED_CLASS);
      body.classList.remove(SCROLLED_CLASS);
      isScrolled = null;
      return;
    }

    if (hero && hero !== found) {
      hero.style.removeProperty('margin-top');
    }
    hero = found;
    body.classList.add(ENABLED_CLASS);
    measure();
    applyScrollState();
  }

  measure();
  applyScrollState();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', measure);

  /* Re-measure once the logo image and web fonts have settled. */
  window.addEventListener('load', measure);

  if (window.ResizeObserver) {
    var observer = new ResizeObserver(measure);
    for (var i = 0; i < sections.length; i++) {
      if (sections[i]) {
        observer.observe(sections[i]);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', refresh);

  /* Homepage sections can be added, removed and reordered without a
     reload in the theme editor. */
  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);
  document.addEventListener('shopify:section:reorder', refresh);
})();
